import React, { createContext, useReducer, useCallback } from 'react';
import { TransactionState, Transaction, TransactionTotals, PaymentMethod } from '../types';
import { apiClient } from '../api/client';

// Initial state
const initialState: TransactionState = {
  transactions: [],
  currentTransaction: null,
  isLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    limit: 10,
    total: 0,
    hasMore: false
  },
  transactionTotals: null
};

// Action types
type TransactionAction =
  | { type: 'GET_TRANSACTIONS_REQUEST' }
  | { type: 'GET_TRANSACTIONS_SUCCESS'; payload: {
      transactions: Transaction[];
      pagination: {
        currentPage: number;
        limit: number;
        total: number;
        hasMore: boolean;
      };
      append: boolean;
    } }
  | { type: 'GET_TRANSACTIONS_FAILURE'; payload: Error }
  | { type: 'GET_ADMIN_TRANSACTIONS_REQUEST' }
  | { type: 'GET_ADMIN_TRANSACTIONS_SUCCESS'; payload: {
      transactions: Transaction[];
      pagination: {
        currentPage: number;
        limit: number;
        total: number;
        hasMore: boolean;
      };
      append: boolean;
    } }
  | { type: 'GET_ADMIN_TRANSACTIONS_FAILURE'; payload: Error }
  | { type: 'GET_TRANSACTION_REQUEST' }
  | { type: 'GET_TRANSACTION_SUCCESS'; payload: Transaction }
  | { type: 'GET_TRANSACTION_FAILURE'; payload: Error }
  | { type: 'CREATE_TRANSACTION_REQUEST' }
  | { type: 'CREATE_TRANSACTION_SUCCESS'; payload: Transaction }
  | { type: 'CREATE_TRANSACTION_FAILURE'; payload: Error }
  | { type: 'UPDATE_TRANSACTION_REQUEST' }
  | { type: 'UPDATE_TRANSACTION_SUCCESS'; payload: Transaction }
  | { type: 'UPDATE_TRANSACTION_FAILURE'; payload: Error }
  | { type: 'RELEASE_PAYMENT_REQUEST' }
  | { type: 'RELEASE_PAYMENT_SUCCESS'; payload: Transaction }
  | { type: 'RELEASE_PAYMENT_FAILURE'; payload: Error }
  | { type: 'GET_TRANSACTION_TOTALS_REQUEST' }
  | { type: 'GET_TRANSACTION_TOTALS_SUCCESS'; payload: TransactionTotals }
  | { type: 'GET_TRANSACTION_TOTALS_FAILURE'; payload: Error }
  | { type: 'CLEAR_ERROR' };

// Reducer
const transactionReducer = (state: TransactionState, action: TransactionAction): TransactionState => {
  switch (action.type) {
    case 'GET_TRANSACTIONS_REQUEST':
    case 'GET_ADMIN_TRANSACTIONS_REQUEST':
    case 'GET_TRANSACTION_REQUEST':
    case 'CREATE_TRANSACTION_REQUEST':
    case 'UPDATE_TRANSACTION_REQUEST':
    case 'RELEASE_PAYMENT_REQUEST':
    case 'GET_TRANSACTION_TOTALS_REQUEST':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'GET_TRANSACTIONS_SUCCESS':
      console.log('TransactionContext Reducer: Processing GET_TRANSACTIONS_SUCCESS with payload:', action.payload);
      
      // Ensure transactions is always an array
      const newTransactions = Array.isArray(action.payload.transactions)
        ? action.payload.transactions
        : [];
        
      if (!Array.isArray(action.payload.transactions)) {
        console.error('TransactionContext Reducer: Non-array transactions in payload:', action.payload.transactions);
      }
      
      const updatedTransactions = action.payload.append
        ? [...state.transactions, ...newTransactions]
        : newTransactions;
        
      console.log('TransactionContext Reducer: Updated transactions array:', updatedTransactions);
      
      return {
        ...state,
        transactions: updatedTransactions,
        pagination: action.payload.pagination,
        isLoading: false,
        error: null,
      };
    case 'GET_ADMIN_TRANSACTIONS_SUCCESS':
      console.log('TransactionContext Reducer: Processing GET_ADMIN_TRANSACTIONS_SUCCESS with payload:', action.payload);
      
      // Ensure transactions is always an array
      const newAdminTransactions = Array.isArray(action.payload.transactions)
        ? action.payload.transactions
        : [];
        
      if (!Array.isArray(action.payload.transactions)) {
        console.error('TransactionContext Reducer: Non-array transactions in payload:', action.payload.transactions);
      }
      
      const updatedAdminTransactions = action.payload.append
        ? [...state.transactions, ...newAdminTransactions]
        : newAdminTransactions;
        
      console.log('TransactionContext Reducer: Updated transactions array:', updatedAdminTransactions);
      
      return {
        ...state,
        transactions: updatedAdminTransactions,
        pagination: action.payload.pagination,
        isLoading: false,
        error: null,
      };
    case 'GET_TRANSACTION_SUCCESS':
      return {
        ...state,
        currentTransaction: action.payload,
        isLoading: false,
        error: null,
      };
    case 'CREATE_TRANSACTION_SUCCESS':
      return {
        ...state,
        transactions: [...state.transactions, action.payload],
        currentTransaction: action.payload,
        isLoading: false,
        error: null,
      };
    case 'UPDATE_TRANSACTION_SUCCESS':
    case 'RELEASE_PAYMENT_SUCCESS':
      return {
        ...state,
        transactions: state.transactions.map((transaction) =>
          transaction.id === action.payload.id ? action.payload : transaction
        ),
        currentTransaction:
          state.currentTransaction && state.currentTransaction.id === action.payload.id
            ? action.payload
            : state.currentTransaction,
        isLoading: false,
        error: null,
      };
    case 'GET_TRANSACTION_TOTALS_SUCCESS':
      return {
        ...state,
        transactionTotals: action.payload,
        isLoading: false,
        error: null,
      };
    case 'GET_TRANSACTIONS_FAILURE':
    case 'GET_ADMIN_TRANSACTIONS_FAILURE':
    case 'GET_TRANSACTION_FAILURE':
    case 'CREATE_TRANSACTION_FAILURE':
    case 'UPDATE_TRANSACTION_FAILURE':
    case 'RELEASE_PAYMENT_FAILURE':
    case 'GET_TRANSACTION_TOTALS_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context
interface TransactionContextType extends TransactionState {
  getTransactions: (page?: number, limit?: number) => Promise<Transaction[]>;
  getAdminTransactions: (incompleteOnly?: boolean, page?: number, limit?: number) => Promise<Transaction[]>;
  getTransaction: (id: string) => Promise<Transaction>;
  createTransaction: (data: Partial<Transaction>) => Promise<Transaction>;
  updateTransaction: (id: string, data: Partial<Transaction>) => Promise<Transaction>;
  releasePayment: (id: string, rating?: number, comment?: string) => Promise<Transaction>;
  withdrawFunds: () => Promise<any>;
  getTransactionTotals: () => Promise<TransactionTotals>;
  clearError: () => void;
}

export const TransactionContext = createContext<TransactionContextType | undefined>(undefined);

// Provider
interface TransactionProviderProps {
  children: React.ReactNode;
}

export const TransactionProvider: React.FC<TransactionProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(transactionReducer, initialState);

  // Get all transactions - memoized with useCallback to prevent infinite loops
  const getTransactions = useCallback(async (page = 1, limit = 10): Promise<Transaction[]> => {
    dispatch({ type: 'GET_TRANSACTIONS_REQUEST' });
    try {
      console.log('TransactionContext: Fetching transactions from API...');
      const response = await apiClient.transactions.getAll(page, limit);
      console.log('TransactionContext: API response received:', response);
      
      // Validate that transactions is an array
      if (!Array.isArray(response)) {
        console.error('TransactionContext: API returned non-array transactions:', response);
        // Return empty array to prevent errors
        return [];
      }
      
      const transactions = response as Transaction[];
      console.log('TransactionContext: Transactions array:', transactions);
      
      // Calculate if there are more transactions (if we got exactly the limit, assume there might be more)
      const hasMore = transactions.length === limit;
      
      dispatch({
        type: 'GET_TRANSACTIONS_SUCCESS',
        payload: {
          transactions: transactions,
          pagination: {
            currentPage: page,
            limit: limit,
            total: transactions.length + (hasMore ? limit : 0), // Estimate total
            hasMore: hasMore
          },
          append: page > 1 // Flag to indicate if we should append or replace
        }
      });
      
      return transactions;
    } catch (error) {
      dispatch({ type: 'GET_TRANSACTIONS_FAILURE', payload: error as Error });
      throw error;
    }
  }, [dispatch]); // Include dispatch in the dependency array

  // Get a transaction by ID - memoized with useCallback
  const getTransaction = useCallback(async (id: string): Promise<Transaction> => {
    dispatch({ type: 'GET_TRANSACTION_REQUEST' });
    try {
      // Handle both synchronous and asynchronous implementations
      const response = apiClient.transactions.getById(id);
      
      // Check if response is a Promise
      const transaction = (response instanceof Promise)
        ? await response as Transaction
        : response as Transaction;
      
      // Validate transaction data
      if (!transaction || typeof transaction !== 'object' || !transaction.id) {
        throw new Error('Invalid transaction data received');
      }
      
      // Handle payment method data
      let paymentMethodData: PaymentMethod | undefined;
      
      // Check if we have a PaymentMethod object
      if (transaction.paymentMethod && typeof transaction.paymentMethod === 'object') {
        paymentMethodData = {
          id: transaction.paymentMethod.id || 'unknown',
          paymentType: transaction.paymentMethod.paymentType || 'unknown',
          lastFour: transaction.paymentMethod.lastFour,
          paymentIntentId: transaction.paymentMethod.paymentIntentId,
          paymentDate: transaction.paymentMethod.paymentDate || new Date().toISOString()
        };
      }
      // Handle legacy format where paymentMethod is a string
      else if (typeof transaction.paymentMethod === 'string' && transaction.paymentMethod) {
        paymentMethodData = {
          id: transaction.paymentMethodId || 'unknown',
          paymentType: transaction.paymentMethod,
          paymentDate: transaction.createdAt || new Date().toISOString()
        };
      }
      
      // Ensure all required fields are present
      const validatedTransaction: Transaction = {
        id: transaction.id,
        sellerId: transaction.sellerId || 'unknown',
        buyerId: transaction.buyerId || 'unknown',
        sellerPublicId: transaction.sellerPublicId,
        buyerPublicId: transaction.buyerPublicId,
        totalAmount: transaction.totalAmount || 0,
        originalAmount: transaction.originalAmount || 0,
        platformFee: transaction.platformFee || 0,
        status: transaction.status || 'pending',
        itemId: transaction.itemId || 'unknown',
        itemDescription: transaction.itemDescription || transaction.itemId || 'Item',
        paymentMethodId: transaction.paymentMethodId,
        paymentMethod: paymentMethodData,
        trackingId: transaction.trackingId,
        createdAt: transaction.createdAt || new Date().toISOString(),
        updatedAt: transaction.updatedAt || new Date().toISOString(),
        lastActivityAt: transaction.lastActivityAt || new Date().toISOString(),
        trackingExpirationTime: transaction.trackingExpirationTime,
        isTrackingExpired: transaction.isTrackingExpired,
        remainingHoursForTracking: transaction.remainingHoursForTracking,
        autoReleaseTime: transaction.autoReleaseTime,
        isReadyForAutoRelease: transaction.isReadyForAutoRelease,
        remainingHoursForAutoRelease: transaction.remainingHoursForAutoRelease,
        isDisputed: transaction.isDisputed,
        hasPreviousDisputes: transaction.hasPreviousDisputes,
      };
      
      console.log('TransactionContext: Validated transaction:', validatedTransaction);

      dispatch({ type: 'GET_TRANSACTION_SUCCESS', payload: validatedTransaction });
      return validatedTransaction;
    } catch (error) {
      console.error('[TransactionContext] Error fetching transaction:', error);
      dispatch({ type: 'GET_TRANSACTION_FAILURE', payload: error as Error });
      throw error;
    }
  }, [dispatch]); // Include only dispatch in the dependency array

  // Create a new transaction - memoized with useCallback
  const createTransaction = useCallback(async (data: Partial<Transaction>): Promise<Transaction> => {
    dispatch({ type: 'CREATE_TRANSACTION_REQUEST' });
    try {
      const transaction = await apiClient.transactions.create(data) as Transaction;
      dispatch({ type: 'CREATE_TRANSACTION_SUCCESS', payload: transaction });
      return transaction;
    } catch (error) {
      dispatch({ type: 'CREATE_TRANSACTION_FAILURE', payload: error as Error });
      throw error;
    }
  }, [dispatch]); // Include dispatch in the dependency array

  // Update a transaction - memoized with useCallback
  const updateTransaction = useCallback(async (id: string, data: Partial<Transaction>): Promise<Transaction> => {
    dispatch({ type: 'UPDATE_TRANSACTION_REQUEST' });
    try {
      const transaction = await apiClient.transactions.update(id, data) as Transaction;
      dispatch({ type: 'UPDATE_TRANSACTION_SUCCESS', payload: transaction });
      return transaction;
    } catch (error) {
      dispatch({ type: 'UPDATE_TRANSACTION_FAILURE', payload: error as Error });
      throw error;
    }
  }, [dispatch]); // Include dispatch in the dependency array

  // Release payment for a transaction - memoized with useCallback
  const releasePayment = useCallback(async (id: string, rating?: number, comment?: string): Promise<Transaction> => {
    dispatch({ type: 'RELEASE_PAYMENT_REQUEST' });
    try {
      const transaction = await apiClient.transactions.releasePayment(id, rating, comment) as Transaction;
      dispatch({ type: 'RELEASE_PAYMENT_SUCCESS', payload: transaction });
      return transaction;
    } catch (error) {
      dispatch({ type: 'RELEASE_PAYMENT_FAILURE', payload: error as Error });
      throw error;
    }
  }, [dispatch]); // Include dispatch in the dependency array

  // Get admin transactions - memoized with useCallback
  const getAdminTransactions = useCallback(async (incompleteOnly = true, page = 1, limit = 10): Promise<Transaction[]> => {
    dispatch({ type: 'GET_ADMIN_TRANSACTIONS_REQUEST' });
    try {
      console.log('TransactionContext: Fetching admin transactions from API...');
      const response = await apiClient.transactions.getAdminTransactions(incompleteOnly, page, limit);
      console.log('TransactionContext: API response received:', response);
      
      // Validate that transactions is an array
      if (!Array.isArray(response)) {
        console.error('TransactionContext: API returned non-array transactions:', response);
        // Return empty array to prevent errors
        return [];
      }
      
      const transactions = response as Transaction[];
      console.log('TransactionContext: Admin transactions array:', transactions);
      
      // Calculate if there are more transactions (if we got exactly the limit, assume there might be more)
      const hasMore = transactions.length === limit;
      
      dispatch({
        type: 'GET_ADMIN_TRANSACTIONS_SUCCESS',
        payload: {
          transactions: transactions,
          pagination: {
            currentPage: page,
            limit: limit,
            total: transactions.length + (hasMore ? limit : 0), // Estimate total
            hasMore: hasMore
          },
          append: page > 1 // Flag to indicate if we should append or replace
        }
      });
      
      return transactions;
    } catch (error) {
      dispatch({ type: 'GET_ADMIN_TRANSACTIONS_FAILURE', payload: error as Error });
      throw error;
    }
  }, [dispatch]); // Include dispatch in the dependency array

  // Get transaction totals - memoized with useCallback
  const getTransactionTotals = useCallback(async (): Promise<TransactionTotals> => {
    dispatch({ type: 'GET_TRANSACTION_TOTALS_REQUEST' });
    try {
      console.log('TransactionContext: Fetching transaction totals from API...');
      const response = await apiClient.transactions.getTransactionTotals();
      console.log('TransactionContext: Transaction totals received:', response);
      
      // Type assertion for the response
      const typedResponse = response as any;
      
      const totals: TransactionTotals = {
        totalPendingRelease: typedResponse.totalPendingRelease || 0,
        totalWithdrawable: typedResponse.totalWithdrawable || 0
      };
      
      dispatch({ type: 'GET_TRANSACTION_TOTALS_SUCCESS', payload: totals });
      return totals;
    } catch (error) {
      console.error('[TransactionContext] Error fetching transaction totals:', error);
      dispatch({ type: 'GET_TRANSACTION_TOTALS_FAILURE', payload: error as Error });
      throw error;
    }
  }, [dispatch]); // Include dispatch in the dependency array

  // Withdraw funds - memoized with useCallback
  const withdrawFunds = useCallback(async (): Promise<any> => {
    dispatch({ type: 'RELEASE_PAYMENT_REQUEST' }); // Reuse the release payment action types
    try {
      console.log('TransactionContext: Initiating funds withdrawal');
      const response = await apiClient.transactions.withdrawFunds();
      console.log('TransactionContext: Withdrawal response:', response);
      
      // After successful withdrawal, refresh transaction totals
      await getTransactionTotals();
      
      dispatch({ type: 'RELEASE_PAYMENT_SUCCESS', payload: {} as Transaction });
      return response;
    } catch (error) {
      console.error('[TransactionContext] Error withdrawing funds:', error);
      dispatch({ type: 'RELEASE_PAYMENT_FAILURE', payload: error as Error });
      throw error;
    }
  }, [dispatch, getTransactionTotals]); // Include dispatch and getTransactionTotals in the dependency array

  // Clear error - memoized with useCallback
  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, [dispatch]); // Include dispatch in the dependency array

  return (
    <TransactionContext.Provider
      value={{
        ...state,
        getTransactions,
        getTransaction,
        createTransaction,
        updateTransaction,
        releasePayment,
        withdrawFunds,
        getAdminTransactions,
        getTransactionTotals,
        clearError,
      }}
    >
      {children}
    </TransactionContext.Provider>
  );
};